import React, { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function getAuthHeaders() {
  const token = localStorage.getItem('tiwistock_access_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export function authFetch(url, options = {}) {
  const token = localStorage.getItem('tiwistock_access_token');
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/auth/me`);
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        const refreshed = await tryRefreshToken();
        if (!refreshed) {
          clearTokens();
          setUser(false);
        }
      }
    } catch {
      setUser(false);
    } finally {
      setChecking(false);
    }
  };

  const tryRefreshToken = async () => {
    const refreshToken = localStorage.getItem('tiwistock_refresh_token');
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.access_token) {
          localStorage.setItem('tiwistock_access_token', data.access_token);
        }
        const meRes = await authFetch(`${API_URL}/api/auth/me`);
        if (meRes.ok) {
          const userData = await meRes.json();
          setUser(userData);
          return true;
        }
      }
    } catch {}
    return false;
  };

  const storeTokens = (data) => {
    if (data.access_token) localStorage.setItem('tiwistock_access_token', data.access_token);
    if (data.refresh_token) localStorage.setItem('tiwistock_refresh_token', data.refresh_token);
  };

  const clearTokens = () => {
    localStorage.removeItem('tiwistock_access_token');
    localStorage.removeItem('tiwistock_refresh_token');
  };

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(formatError(data.detail));
    storeTokens(data);
    setUser(data);
    migratePortfolio();
    return data;
  };

  const register = async (email, password, name) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(formatError(data.detail));
    storeTokens(data);
    setUser(data);
    migratePortfolio();
    return data;
  };

  const logout = async () => {
    await authFetch(`${API_URL}/api/auth/logout`, { method: 'POST' });
    clearTokens();
    setUser(false);
  };

  const migratePortfolio = async () => {
    const deviceId = localStorage.getItem('tiwistock_device_id');
    if (!deviceId) return;
    try {
      await authFetch(`${API_URL}/api/portfolio/migrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_id: deviceId })
      });
    } catch (err) {
      console.error('Portfolio migration failed:', err);
    }
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
